import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { ArrowRight } from 'lucide-react';
import PageHero from '../../components/ui/page-hero';
import SolutionSection from '../../components/solutions/SolutionSection';
import SolutionFAQ from '../../components/solutions/SolutionFAQ';
import SolutionStats from '../../components/solutions/SolutionStats';
import StickyCTA from '../../components/solutions/StickyCTA';
import solutionsData from '../../content/solutions/solutions.json';
import '../../components/services/services.css';

const Solutions = () => {
  useEffect(() => {
    // Smooth scroll behavior for better UX
    document.documentElement.style.scrollBehavior = 'smooth';
    
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{solutionsData.meta.title}</title>
        <meta name="description" content={solutionsData.meta.description} />
        <meta name="keywords" content={solutionsData.meta.keywords} />
        <meta property="og:title" content={solutionsData.meta.title} />
        <meta property="og:description" content={solutionsData.meta.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://envael.com/solutions" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={solutionsData.meta.title} />
        <meta name="twitter:description" content={solutionsData.meta.description} />
        <link rel="canonical" href="https://envael.com/solutions" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Page Hero with Navigation */}
        <PageHero 
          pageName="Solutions" 
          subtitle={solutionsData.hero.subtitle}
          size="large"
          fullHeight={true}
          enableSpline={true}
          ctaButtons={solutionsData.hero.cta}
        />

        {/* Industry Stats */}
        <SolutionStats stats={solutionsData.stats} />

        {/* Solutions Sections */}
        {solutionsData.solutions.map((solution, index) => (
          <div key={solution.id}>
            <SolutionSection 
              solution={solution} 
              index={index}
            />
            
            {/* FAQ Section for each solution */}
            <SolutionFAQ 
              faqs={solution.faq} 
              solutionTitle={solution.title}
            />
          </div>
        ))}

        {/* Final CTA Section */}
        <section className="py-24 bg-gradient-to-br from-primary/5 via-background to-primary/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                Ready to Boost Your Business?
              </h2>
              <p className="text-xl text-muted-foreground mb-12 leading-relaxed">
                Join 10,000+ businesses that have transformed their operations with our software solutions. 
                Start your free trial today and see the difference.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <button className="px-8 py-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold text-lg group">
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform inline" />
                </button>
                
                <button className="px-8 py-4 border-2 border-primary/20 text-primary rounded-lg hover:bg-primary/5 transition-colors font-semibold text-lg">
                  View All Demos
                </button>
              </div>

              {/* Software Trust Indicators */}
              <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">7</div>
                  <div className="text-sm text-muted-foreground">Software Products</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">10K+</div>
                  <div className="text-sm text-muted-foreground">Active Users</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">99.9%</div>
                  <div className="text-sm text-muted-foreground">Uptime Guarantee</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">24/7</div>
                  <div className="text-sm text-muted-foreground">Support Available</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sticky CTA */}
        <StickyCTA 
          title={solutionsData.stickyCta.title}
          buttons={solutionsData.stickyCta.buttons}
        />
      </div>
    </>
  );
};

export default Solutions;